import { useLLMProxyServiceContext } from "contexts/llmProxySerivceContext";
import { useGetOrderNarrative } from "hooks/useGetOrderNarrative";
import { Message } from "hooks/useInference/types";
import { useSpeechRecognition } from "hooks/useSpeechRecognition";
import { useSpeech } from "hooks/useSpeech";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAlert from "utils/alert";
import { v4 as uuidv4 } from 'uuid';
import { getLenguageCode } from "utils/react-i18next-utils";
import { ProcessedImage } from "hooks/useDocumentAttachment";
import { ASSISTANT_CONVERSATION_KEY } from "utils/constants";

type Params = {
  onConversationUpdated?: () => void
}

const CONVERSATION_STORAGE_KEY = ASSISTANT_CONVERSATION_KEY;

const loadStoredConversation = (): Message[] => {
  try {
    const raw = localStorage.getItem(CONVERSATION_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((message) => ({
      ...message,
      questionTime: new Date(message.questionTime),
      isLoading: false,
    }));
  } catch {
    return [];
  }
};

export const useInferenceV2 = ({ onConversationUpdated }: Params) => {
  const [isLoadingInference, setIsLoadingInference] = useState<boolean>(false);
  const [messageList, setMessageList] = useState<Message[]>(loadStoredConversation);
  const messageListRef = useRef<Message[]>(messageList);
  const [htmlContent, setHtmlContent] = useState<string>("");
  const { id: orderId } = useParams<{ id: string }>();
  const { llmServiceRef, clearSignal, promptSelection, triggerUsageUpdate } = useLLMProxyServiceContext();
  const [currentPlaying, setCurrentPlaying] = useState<string>();
  const playAudioEnabledRef = useRef(false);
  const location = useLocation();
  const { i18n } = useTranslation();
  const pendingImagesRef = useRef<ProcessedImage[]>([]);
  const handledClearSignalRef = useRef(0);
  const showAlert = useAlert();

  const currentLanguage = getLenguageCode(i18n.language);

  useEffect(() => {
    messageListRef.current = messageList;
    if (messageList.some((message) => message.isLoading)) return;

    try {
      localStorage.setItem(CONVERSATION_STORAGE_KEY, JSON.stringify(messageList));
    } catch {
      // Ignore storage write failures (e.g. quota exceeded).
    }
  }, [messageList]);

  useEffect(() => {
    const handlePageHide = () => {
      const messagesToSave = messageListRef.current.map((msg) => ({
        ...msg,
        isLoading: false,
      }));
      try {
        localStorage.setItem(CONVERSATION_STORAGE_KEY, JSON.stringify(messagesToSave));
      } catch {
        // Ignore storage write failures
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  const {
    getSpeech,
    stopSpeechAudio,
    isSpeechLoading,
    abortGetSpeech,
    abortGetSpeechSilent,
  } = useSpeech({
    onSpeechAudioEnd() {
      setCurrentPlaying(undefined);
    },
    onSpeechAudioError(error) {
      if (error?.name !== 'AbortError') {
        setCurrentPlaying(undefined);
        showAlert('Failed to synthesize audio', 'error');
      }
    },
  });

  const playSpeech = useCallback(
    ({ messageId, text }: { messageId: string, text: string }, opts?: { onComplete?: () => void }) => {
      stopSpeechAudio();
      abortGetSpeechSilent();
      setCurrentPlaying(messageId);
      getSpeech({ text, language: currentLanguage }, {
        onComplete: opts?.onComplete,
        onError: (error) => {
          if (error?.name !== 'AbortError') {
            setCurrentPlaying(undefined);
            showAlert('Failed to synthesize audio', 'error');
          }
          if (opts?.onComplete) {
            opts.onComplete();
          }
        },
      });
    },
    [stopSpeechAudio, abortGetSpeechSilent, currentLanguage, getSpeech, showAlert]
  );

  const stopSpeech = useCallback(() => {
    setCurrentPlaying(undefined);
    stopSpeechAudio();
    abortGetSpeech();
  }, [stopSpeechAudio, abortGetSpeech]);

  const toogleAudioEnable = () => {
    playAudioEnabledRef.current = !playAudioEnabledRef.current;
  };

  const clearConversation = useCallback(async () => {
    await llmServiceRef.current?.clearHistory();
    setMessageList([]);
  }, [llmServiceRef]);

  useEffect(() => {
    if (clearSignal <= handledClearSignalRef.current) return;
    handledClearSignalRef.current = clearSignal;
    setMessageList([]);
  }, [clearSignal]);

  useGetOrderNarrative({
    orderId,
    queryConfig: {
      enabled: !!orderId && location.pathname.includes("orders"),
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        llmServiceRef.current?.setCurrentOrderNarrative(data.getOrder.narrative);
      }
    }
  });

  const onNewMessage = useCallback((id: string) => {
    return (message: string) => {
      const updateMessageList = () => {
        setMessageList(prevMessageList => {
          const messageIndex = prevMessageList.findIndex((m) => m.id === id);
          if (messageIndex === -1) return prevMessageList;
          const messageListCopy = [...prevMessageList];
          messageListCopy[messageIndex] = {
            ...messageListCopy[messageIndex],
            response: message,
            isLoading: false,
          };
          return messageListCopy;
        });
        if (onConversationUpdated) onConversationUpdated();
      };

      if (playAudioEnabledRef.current) {
        playSpeech({ messageId: id, text: message }, { onComplete: updateMessageList });
        return;
      }
      updateMessageList();
    };
  }, [onConversationUpdated, playSpeech]);

  const onMessageDelta = useCallback((id: string) => {
    return (delta: string) => {
      setMessageList(prevMessageList => {
        const messageIndex = prevMessageList.findIndex((m) => m.id === id);
        if (messageIndex === -1) return prevMessageList;
        const messageListCopy = [...prevMessageList];
        messageListCopy[messageIndex] = {
          ...messageListCopy[messageIndex],
          response: `${messageListCopy[messageIndex].response || ''}${delta}`,
        };
        return messageListCopy;
      });
    };
  }, []);

  const onMessageIncomplete = useCallback((id: string) => {
    return (notice: string) => {
      setMessageList(prevMessageList => {
        const messageIndex = prevMessageList.findIndex((m) => m.id === id);
        if (messageIndex === -1) return prevMessageList;
        const messageListCopy = [...prevMessageList];
        messageListCopy[messageIndex] = {
          ...messageListCopy[messageIndex],
          isLoading: false,
          incompleteNotice: notice,
        };
        return messageListCopy;
      });
      if (onConversationUpdated) onConversationUpdated();
    };
  }, [onConversationUpdated]);

  const onStreamStart = useCallback((id: string) => {
    return () => {
      setMessageList(prevMessageList => {
        const messageIndex = prevMessageList.findIndex((m) => m.id === id);
        if (messageIndex === -1) return prevMessageList;
        const messageListCopy = [...prevMessageList];
        messageListCopy[messageIndex] = {
          ...messageListCopy[messageIndex],
          isLoading: true,
        };
        return messageListCopy;
      });
    };
  }, []);

  const onThoughtUpdate = useCallback((id: string) => {
    return (thought: string) => {
      setMessageList(prevMessageList => {
        const messageIndex = prevMessageList.findIndex((m) => m.id === id);
        if (messageIndex === -1) return prevMessageList;
        const messageListCopy = [...prevMessageList];
        messageListCopy[messageIndex] = {
          ...messageListCopy[messageIndex],
          thoughts: `${(messageListCopy[messageIndex].thoughts || '') + thought}\n`,
        };
        return messageListCopy;
      });
    };
  }, []);

  const onToolCallUpdate = useCallback((id: string) => {
    return (toolCalls: { id: string, name: string, args: string, result?: string, status: 'running' | 'completed' | 'error' }[]) => {
      setMessageList(prevMessageList => {
        const messageIndex = prevMessageList.findIndex((m) => m.id === id);
        if (messageIndex === -1) return prevMessageList;
        const messageListCopy = [...prevMessageList];
        const existingToolCalls = messageListCopy[messageIndex].toolCalls || [];
        toolCalls.forEach((update) => {
          const existingIndex = existingToolCalls.findIndex((tc) => tc.id === update.id);
          if (existingIndex > -1) {
            existingToolCalls[existingIndex] = {
              ...existingToolCalls[existingIndex],
              ...update,
            };
          } else {
            existingToolCalls.push(update);
          }
        });
        messageListCopy[messageIndex] = {
          ...messageListCopy[messageIndex],
          toolCalls: [...existingToolCalls],
        };
        return messageListCopy;
      });
    };
  }, []);

  const subAgentStateMapRef = useRef<Map<string, { id: string, name: string, args: string, status: 'running' | 'completed' | 'error', result?: string }>>(new Map());

  const onSubAgentUpdate = useCallback((messageId: string) => {
    return (update: any) => {
      const { subagentName, status, message } = update;
      const callId = subagentName;

      const mapStatus = (s: string): 'running' | 'completed' | 'error' => {
        if (s === 'completed') return 'completed';
        if (s === 'error') return 'error';
        return 'running';
      };

      setMessageList(prevMessageList => {
        const messageIndex = prevMessageList.findIndex((m) => m.id === messageId);
        if (messageIndex === -1) return prevMessageList;
        const messageListCopy = [...prevMessageList];
        const existingSubAgentCalls = messageListCopy[messageIndex].subAgentCalls || [];

        const existingIndex = existingSubAgentCalls.findIndex((sac) => sac.id === callId);
        const updated = {
          id: callId,
          name: subagentName,
          args: status,
          status: mapStatus(status),
          result: message,
        };

        if (existingIndex > -1) {
          existingSubAgentCalls[existingIndex] = {
            ...existingSubAgentCalls[existingIndex],
            ...updated,
          };
        } else {
          existingSubAgentCalls.push(updated);
        }

        messageListCopy[messageIndex] = {
          ...messageListCopy[messageIndex],
          subAgentCalls: [...existingSubAgentCalls],
        };
        return messageListCopy;
      });
    };
  }, []);

  const sendQuestion = useCallback(async (prompt: string, images?: ProcessedImage[]) => {
    const id: string = uuidv4();
    const hasImages = images && images.length > 0;

    const newMessage = {
      id,
      questionTime: new Date(),
      isLoading: true,
      response: "",
      text: hasImages ? `[Document] ${prompt || 'Processing attached images...'}` : prompt,
      images: images ? images.map(image => image.original) : undefined
    };

    setMessageList((prevMessageList) => [...prevMessageList, newMessage]);

    const onNewMessageCallback = onNewMessage(id);
    const onThoughtCallback = onThoughtUpdate(id);
    const onToolCallCallback = onToolCallUpdate(id);
    const onSubAgentCallCallback = onSubAgentUpdate(id);
    const onMessageDeltaCallback = onMessageDelta(id);
    const onMessageIncompleteCallback = onMessageIncomplete(id);
    const onStreamStartCallback = onStreamStart(id);

    try {
      setIsLoadingInference(true);
      const imageParam = hasImages
        ? images.map(image => ({
          baseUrl: image.postProcessed,
          sendAs: image.sendAs,
          label: image.label
        }))
        : undefined;
      const llmService = llmServiceRef.current;

      if (promptSelection.kind === "custom") {
        const selectedPrompt = promptSelection.prompt;
        llmService?.setPrompts([selectedPrompt]);
      }

      const onAttachmentErrorCallback = (error: string) => {
        showAlert(error, 'error');
      };

      await llmService?.invoke(
        prompt,
        onNewMessageCallback,
        {
          images: imageParam,
          onThought: onThoughtCallback,
          onToolCall: onToolCallCallback,
          onMessageDelta: onMessageDeltaCallback,
          onStreamStart: onStreamStartCallback,
          onSubAgentUpdate: onSubAgentCallCallback,
          onAttachmentError: onAttachmentErrorCallback,
          onMessageIncomplete: onMessageIncompleteCallback,
        }
      );
      setIsLoadingInference(false);
      triggerUsageUpdate();
    } catch (error) {
      setIsLoadingInference(false);
      setMessageList(prevMessageList => {
        const messageIndex = prevMessageList.findIndex((m) => m.id === id);
        if (messageIndex === -1) return prevMessageList;
        const messageListCopy = [...prevMessageList];
        messageListCopy[messageIndex] = {
          ...messageListCopy[messageIndex],
          isLoading: false,
          response: messageListCopy[messageIndex].response || 'An error occurred.',
        };
        return messageListCopy;
      });
    }
  }, [
    promptSelection,
    llmServiceRef,
    onNewMessage,
    onThoughtUpdate,
    onToolCallUpdate,
    onMessageDelta,
    onMessageIncomplete,
    onStreamStart,
    onSubAgentUpdate,
    showAlert,
    triggerUsageUpdate
  ]);

  const {
    isRecording,
    startRecordAudio,
    stopRecordAudio
  } = useSpeechRecognition({
    onRecordStart() {
      stopSpeech();
    },
    onRecordStop: async (text) => {
      setIsLoadingInference(true);

      if (text) {
        sendQuestion(text, pendingImagesRef.current);
      }

      pendingImagesRef.current = [];
    }
  });

  const submitRecordedAudio = useCallback((images: ProcessedImage[]) => {
    pendingImagesRef.current = images;
    stopRecordAudio();
  }, [stopRecordAudio]);

  return {
    htmlContent,
    isLoadingInference,
    sendQuestion,
    messageList,
    currentPlaying,
    playSpeech,
    stopSpeech,
    isSpeechLoading,
    toogleAudioEnable,
    isRecording,
    startRecordAudio,
    submitRecordedAudio,
    clearConversation,
  };
};
